import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ParameterDescriptor } from '@/redux/internal-services/agentsApi';
import {
  AgentFormValues,
  SectionCard,
  DescriptorField,
  inputCls,
  areaCls,
  labelCls,
} from './agentShared';
import { Badge } from '@/components/UI/Badge';
import { Switch } from '@/components/UI/Switch';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';

interface OverviewTabProps {
  isEdit: boolean;
  paramGroups: Record<string, ParameterDescriptor[]>;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ isEdit, paramGroups }) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgentFormValues>();

  const agentType = watch('agent_type');
  const isWorkflow = agentType === 'workflow';
  const modelParams = watch('model_parameters') || {};

  const setParam = (key: string, value: unknown) =>
    setValue('model_parameters', { ...modelParams, [key]: value }, { shouldDirty: true });

  const paramDescriptors = paramGroups['model_parameters'] || [];

  return (
    <>
      {/* Identity */}
      <SectionCard title="Identity" desc="How this agent is named, typed, and triggered.">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>
              Agent name <span className="text-danger-text">*</span>
            </label>
            <input
              className={fieldClass({ error: Boolean(errors.agent_name) })}
              placeholder="e.g. SAM, ScheduleBot"
              {...register('agent_name')}
            />
            {errors.agent_name && (
              <p className="mt-1 text-xs text-danger-text">{errors.agent_name.message}</p>
            )}
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              className={areaCls}
              rows={2}
              placeholder="Short description of what this agent does."
              {...register('description')}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>
                Agent type
                {isEdit && (
                  <span className="ml-1 text-xs text-ui-text3 font-normal">— fixed at creation</span>
                )}
              </label>
              {isEdit ? (
                <div className={cn(inputCls, 'flex cursor-default items-center text-ui-text2')}>
                  {agentType === 'workflow' ? 'Workflow (Automation)' : 'Conversational (Chat)'}
                </div>
              ) : (
                <select className={inputCls} {...register('agent_type')}>
                  <option value="conversational">Conversational (Chat)</option>
                  <option value="workflow">Workflow (Automation)</option>
                </select>
              )}
            </div>

            {isWorkflow && (
              <div>
                <label className={labelCls}>Trigger type</label>
                <select className={inputCls} {...register('trigger_type')}>
                  <option value="manual">Manual</option>
                  <option value="schedule">Schedule</option>
                  <option value="webhook">Webhook</option>
                  <option value="event">Event</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Status. Provider / model selection lives in Platform > Configurations (model routing). */}
      <SectionCard title="Status" desc="Provider and model are configured centrally in Platform Configurations.">
        <div className="space-y-5">

          {/* Status */}
          <div>
            <label className={labelCls}>Status</label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <div className="flex h-10 items-center gap-3">
                  <Switch
                    checked={field.value === 'active'}
                    onCheckedChange={(on) => field.onChange(on ? 'active' : 'disabled')}
                    aria-label="Agent status"
                  />
                  <Badge variant={field.value === 'active' ? 'success' : 'neutral'}>
                    {field.value === 'active' ? 'Active' : 'Disabled'}
                  </Badge>
                  <span className="text-xs text-ui-text3">Click to toggle</span>
                </div>
              )}
            />
          </div>

        </div>
      </SectionCard>

      {/* Classification */}
      <SectionCard title="Classification" desc="Used for filtering and discovery in the agent catalogue.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Category</label>
            <input
              className={inputCls}
              placeholder="e.g. Sales, Support, Billing"
              {...register('category')}
            />
          </div>
          <div>
            <label className={labelCls}>
              Tags <span className="text-ui-text3 font-normal text-xs">— comma-separated</span>
            </label>
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <input
                  className={inputCls}
                  placeholder="e.g. crm, voice, demo"
                  value={(field.value || []).join(', ')}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                    )
                  }
                />
              )}
            />
          </div>
        </div>
      </SectionCard>

      {/* Model parameters */}
      {paramDescriptors.length > 0 && (
        <SectionCard
          title="Model parameters"
          desc="Schema-driven tuning for the underlying model. These defaults apply unless overridden."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {paramDescriptors.map((d) => (
              <DescriptorField
                key={d.key}
                d={d}
                value={modelParams[d.key] ?? d.default}
                onChange={(v) => setParam(d.key, v)}
              />
            ))}
          </div>
        </SectionCard>
      )}
    </>
  );
};

export default OverviewTab;
